import { PublishedSiteSnapshot, SitePage, BlogPost, YachtItem } from '../types';

export interface HeadSeoOptions {
  siteName: string;
  pageTitle?: string;
  tagline?: string;
  description?: string;
  canonicalUrl: string;
  imageUrl?: string;
  industry?: string;
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
  isArticle?: boolean;
  phone?: string;
  email?: string;
  address?: string;
  yachts?: YachtItem[];
  blogPosts?: BlogPost[];
}

/**
 * Production SEO Engine
 * Manages Dynamic Head Tags, Canonical URLs, Open Graph, Twitter Cards, Schema.org (JSON-LD),
 * and Sitemap XML generation for all published websites.
 */
export class SeoEngine {
  /**
   * Dynamically updates the document head with full meta tags, canonical link,
   * Open Graph, Twitter Card, and JSON-LD Structured Data.
   */
  public static updateHead(options: HeadSeoOptions): void {
    if (typeof document === 'undefined') return;

    const {
      siteName,
      pageTitle,
      tagline,
      description,
      canonicalUrl,
      imageUrl,
      industry = 'yacht_rental',
      isArticle = false,
      publishedTime,
      modifiedTime,
      authorName,
      phone,
      email,
      address,
      yachts = [],
    } = options;

    // 1. Hierarchical Title: "Page Title | Site Name" or "Site Name — Tagline"
    let fullTitle = siteName;
    if (pageTitle && pageTitle.toLowerCase() !== 'ana sayfa' && pageTitle !== siteName) {
      fullTitle = `${pageTitle} | ${siteName}`;
    } else if (tagline) {
      fullTitle = `${siteName} — ${tagline}`;
    } else {
      fullTitle = `${siteName} — Resmi Web Sitesi`;
    }
    document.title = fullTitle;

    // Helper to safely set or create <meta> tags
    const setMetaTag = (selector: string, attr: 'name' | 'property', key: string, content: string) => {
      if (!content) return;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, key);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // 2. Standard Meta Description
    const metaDesc =
      description ||
      `${siteName} resmi web sitesi. Profesyonel hizmetler, güncel bilgiler ve rezervasyon detayları.`;
    setMetaTag('meta[name="description"]', 'name', 'description', metaDesc);

    // 3. Canonical URL (Strict format: primary domain + path, no query params)
    let cleanCanonical = canonicalUrl;
    try {
      const parsedUrl = new URL(canonicalUrl);
      cleanCanonical = `${parsedUrl.origin}${parsedUrl.pathname}`;
    } catch {
      cleanCanonical = canonicalUrl.split('?')[0].split('#')[0];
    }
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = cleanCanonical;

    // 4. Open Graph Tags
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', metaDesc);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', cleanCanonical);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', isArticle ? 'article' : 'website');
    setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', siteName);
    if (imageUrl) {
      setMetaTag('meta[property="og:image"]', 'property', 'og:image', imageUrl);
    }

    // 5. Twitter Card Tags
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', metaDesc);
    if (imageUrl) {
      setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);
    }

    // 6. Structured Data (JSON-LD)
    const jsonLdData = this.buildStructuredData({
      siteName,
      canonicalUrl: cleanCanonical,
      description: metaDesc,
      imageUrl,
      industry,
      phone,
      email,
      address,
      yachts,
    });

    let scriptTag = document.getElementById('adios-structured-data') as HTMLScriptElement;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'adios-structured-data';
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.text = JSON.stringify(jsonLdData, null, 2);
  }

  /**
   * Generates Industry-Specific Schema.org JSON-LD Objects
   */
  public static buildStructuredData(params: {
    siteName: string;
    canonicalUrl: string;
    description: string;
    imageUrl?: string;
    industry: string;
    phone?: string;
    email?: string;
    address?: string;
    yachts?: YachtItem[];
  }): any {
    const { siteName, canonicalUrl, description, imageUrl, industry, phone, email, address, yachts = [] } = params;

    // Determine specific schema type based on industry
    let industryType = 'LocalBusiness';
    if (industry === 'yacht_rental' || industry === 'marina') {
      industryType = 'BoatReservation'; // Or LodgingBusiness / Service
    } else if (industry === 'hotel' || industry === 'villa' || industry === 'tourism') {
      industryType = 'LodgingBusiness';
    } else if (industry === 'restaurant' || industry === 'cafe') {
      industryType = 'Restaurant';
    } else if (industry === 'corporate' || industry === 'holding') {
      industryType = 'Organization';
    }

    const graph: any[] = [
      {
        '@type': 'WebSite',
        '@id': `${canonicalUrl}#website`,
        url: canonicalUrl,
        name: siteName,
        description,
        publisher: {
          '@id': `${canonicalUrl}#organization`,
        },
      },
      {
        '@type': ['Organization', industryType],
        '@id': `${canonicalUrl}#organization`,
        name: siteName,
        url: canonicalUrl,
        description,
        logo: imageUrl || undefined,
        telephone: phone || undefined,
        email: email || undefined,
        address: address
          ? {
              '@type': 'PostalAddress',
              streetAddress: address,
              addressCountry: 'TR',
            }
          : undefined,
      },
    ];

    // Add Yacht Rental Fleet Schema if yacht items exist
    if (yachts.length > 0) {
      yachts.forEach((y) => {
        if (!y.isDeleted && y.status !== 'MAINTENANCE') {
          graph.push({
            '@type': 'Product',
            '@id': `${canonicalUrl}/yatlar/${y.slug || y.id}#product`,
            name: y.name,
            description: y.description || `${y.name} kiralık lüks yat ve mavi yolculuk teknesi.`,
            image: y.coverImage || y.imageUrl || (y.gallery && y.gallery[0]) || '',
            offers: {
              '@type': 'Offer',
              price: y.pricePerDay,
              priceCurrency: y.currency || 'EUR',
              availability: 'https://schema.org/InStock',
              url: `${canonicalUrl}/yatlar/${y.slug || y.id}`,
            },
          });
        }
      });
    }

    return {
      '@context': 'https://schema.org',
      '@graph': graph,
    };
  }

  /**
   * Generates RFC-compliant XML Sitemap for a published website.
   * Includes home page, published pages, published blogs, and published yachts.
   */
  public static generateSitemapXml(site: PublishedSiteSnapshot | any, canonicalDomain: string): string {
    const host = canonicalDomain.startsWith('http') ? canonicalDomain : `https://${canonicalDomain}`;
    const nowIso = new Date().toISOString().split('T')[0];

    const urls: Array<{ loc: string; lastmod: string; changefreq: string; priority: string }> = [];

    // 1. Home Page
    urls.push({
      loc: `${host}/`,
      lastmod: site.publishedAt ? new Date(site.publishedAt).toISOString().split('T')[0] : nowIso,
      changefreq: 'daily',
      priority: '1.0',
    });

    // 2. Published Site Pages
    const pages: SitePage[] = site.pages || [];
    pages.forEach((page) => {
      if (page && !page.isDeleted && page.isPublished !== false && !page.isHomePage) {
        const slug = page.slug?.startsWith('/') ? page.slug : `/${page.slug}`;
        urls.push({
          loc: `${host}${slug}`,
          lastmod: page.updatedAt ? new Date(page.updatedAt).toISOString().split('T')[0] : nowIso,
          changefreq: 'weekly',
          priority: '0.8',
        });
      }
    });

    // 3. Published Blog Posts
    const blogPosts: BlogPost[] = site.blogPosts || [];
    blogPosts.forEach((post) => {
      if (post && post.status === 'PUBLISHED' && !post.isDeleted && post.slug) {
        urls.push({
          loc: `${host}/blog/${post.slug}`,
          lastmod: post.publishedAt
            ? new Date(post.publishedAt).toISOString().split('T')[0]
            : nowIso,
          changefreq: 'monthly',
          priority: '0.7',
        });
      }
    });

    // 4. Published Yacht Detail Pages (Strictly real yachts, published only)
    const yachts: YachtItem[] = site.yachts || [];
    yachts.forEach((yacht) => {
      if (yacht && !yacht.isDeleted && yacht.status !== 'MAINTENANCE') {
        const yachtSlug = yacht.slug || yacht.id;
        urls.push({
          loc: `${host}/yatlar/${yachtSlug}`,
          lastmod: yacht.updatedAt
            ? new Date(yacht.updatedAt).toISOString().split('T')[0]
            : nowIso,
          changefreq: 'weekly',
          priority: '0.8',
        });
      }
    });

    // Build standard XML
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n`;

    urls.forEach((u) => {
      xml += `  <url>\n`;
      xml += `    <loc>${u.loc}</loc>\n`;
      xml += `    <lastmod>${u.lastmod}</lastmod>\n`;
      xml += `    <changefreq>${u.changefreq}</changefreq>\n`;
      xml += `    <priority>${u.priority}</priority>\n`;
      xml += `  </url>\n`;
    });

    xml += `</urlset>\n`;
    return xml;
  }

  /**
   * Generates standard robots.txt for a website
   */
  public static generateRobotsTxt(canonicalDomain: string, isPublished = true): string {
    const host = canonicalDomain.startsWith('http') ? canonicalDomain : `https://${canonicalDomain}`;
    if (!isPublished) {
      return `User-agent: *\nDisallow: /\n`;
    }
    return `User-agent: *\nAllow: /\nDisallow: /api/\nDisallow: /admin\nSitemap: ${host}/sitemap.xml\n`;
  }
}
